import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Box,
  Card,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  CircularProgress,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import EnhancedTableHead from '../EnhancedTableHead';
import AssignWasherDialog from './AssignWasherDialog';

const useStyles = makeStyles({
  tableCell: {
    cursor: 'pointer',
  },
  loader: {
    position: 'absolute !important',
    top: '20px',
    left: '45%',
  },
  tableBody: {
    position: 'relative',
    height: '60px',
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
});

const headCells = [
  { id: 'no', numeric: false, disablePadding: false, label: 'No' },
  {
    id: 'service_name',
    numeric: false,
    disablePadding: false,
    label: 'Service Name',
    sortable: true,
  },
  {
    id: 'washer_firstname',
    numeric: false,
    disablePadding: false,
    label: 'Washer Name',
    sortable: true,
  },
  {
    id: 'washer_email',
    numeric: false,
    disablePadding: false,
    label: 'Washer Email',
    sortable: true,
  },
  {
    id: 'client_firstname',
    numeric: false,
    disablePadding: false,
    label: 'User Name',
    sortable: true,
  },
  {
    id: 'client_email',
    numeric: false,
    disablePadding: false,
    label: 'User Email',
    sortable: true,
  },
  {
    id: 'status_order',
    numeric: false,
    disablePadding: false,
    label: 'Booking Status',
    sortable: true,
  },
  {
    id: 'total_price',
    numeric: false,
    disablePadding: false,
    label: 'Price',
    sortable: true,
  },
  {
    id: 'creation_date',
    numeric: false,
    disablePadding: false,
    label: 'Date',
    sortable: true,
  },
  {
    id: 'invoice_url',
    numeric: false,
    disablePadding: false,
    label: 'Invoice',
    sortable: false,
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: 'Action',
    sortable: false,
  },
];

function descendingComparator(a, b, orderBy) {
  const aValue = orderBy === 'total_price' ? getTotalPrice(a) : a[orderBy];
  const bValue = orderBy === 'total_price' ? getTotalPrice(b) : b[orderBy];

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const formatPrice = (value) => {
  const numericValue = Number(value || 0);
  return `${Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00'}€`;
};

const getTotalPrice = (job) => {
  const totalPrice = Number(job.total_price);
  if (Number.isFinite(totalPrice)) {
    return totalPrice;
  }

  const basePrice = Number(job.price || 0);
  const extraPrice = Number(job.extra_price || 0);
  const cancelPrice = Number(job.cancel_price || 0);

  if (job.status_order === 'refund' && cancelPrice > 0) {
    return cancelPrice;
  }

  return basePrice + extraPrice;
};

const getAdditionalInvoiceCount = (job) => {
  const count = Number(job.additional_invoice_count || 0);
  return Number.isFinite(count) ? count : 0;
};

const JobsListResults = ({
  jobs,
  handleRow,
  loading,
  pagerInfo,
  handlePagerInfo,
  onAssigned,
  ...rest
}) => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [assignTarget, setAssignTarget] = useState(null); // {orderId, lat, long, service_id}

  const handleLimitChange = (event) => {
    handlePagerInfo({
      limit: event.target.value,
      page: 1,
      totalSize: 0,
    });
  };

  const handlePageChange = (event, newPage) => {
    handlePagerInfo({
      ...pagerInfo,
      page: newPage + 1,
    });
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const goToEdit = (id) => {
    navigate(`/app/jobs/edit/${id}`, { state: id });
  };

  const renderInvoiceCell = (job) => {
    if (!job.invoice_url) {
      return '-';
    }

    const additionalInvoiceCount = getAdditionalInvoiceCount(job);

    return (
      <span>
        <a href={job.invoice_url} target="_blank" rel="noreferrer">
          {job.invoice_reference || 'Invoice'}
        </a>
        {additionalInvoiceCount > 0 && (
          <span
            style={{
              marginLeft: 8,
              padding: '2px 6px',
              borderRadius: 12,
              backgroundColor: '#E53935',
              color: '#FFFFFF',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            +{additionalInvoiceCount}
          </span>
        )}
      </span>
    );
  };

  return (
    <Card {...rest}>
      <PerfectScrollbar>
        <Box sx={{ minWidth: 1050 }}>
          <Table>
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody className={classes.tableBody}>
              {loading && <CircularProgress className={classes.loader} />}
              {!loading &&
                jobs &&
                stableSort(jobs, getComparator(order, orderBy)).map(
                  (user, index) => (
                    <TableRow hover key={user.id}>
                      <TableCell>
                        {(pagerInfo.page - 1) * pagerInfo.limit + index + 1}
                      </TableCell>
                      <TableCell>{user.service_name}</TableCell>
                      <TableCell>
                        {user.washer_firstname} {user.washer_lastname}
                      </TableCell>
                      <TableCell>{user.washer_email}</TableCell>
                      <TableCell>
                        {user.client_firstname} {user.client_lastname}
                      </TableCell>
                      <TableCell>{user.client_email}</TableCell>
                      <TableCell>{user.status_order}</TableCell>
                      <TableCell>{formatPrice(getTotalPrice(user))}</TableCell>
                      <TableCell>{user.creation_date}</TableCell>
                      <TableCell>{renderInvoiceCell(user)}</TableCell>
                      <TableCell>
                        <EditIcon
                          style={{ cursor: 'pointer' }}
                          onClick={() => goToEdit(user.id)}
                        />
                        {user.status_order === 'searching' && (
                          <AssignmentIndIcon
                            titleAccess="Manually assign washer"
                            style={{ cursor: 'pointer', marginLeft: 8, color: '#2270bc' }}
                            onClick={() => setAssignTarget({
                              orderId: user.id,
                              lat: user.lat,
                              long: user.long,
                              service_id: user.service_id,
                            })}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  )
                )}
              {!loading && !jobs && <p className={classes.loader}>No Data</p>}
            </TableBody>
          </Table>
        </Box>
      </PerfectScrollbar>
      <TablePagination
        component="div"
        count={pagerInfo ? pagerInfo.totalSize : 0}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleLimitChange}
        page={pagerInfo ? pagerInfo.page - 1 : 0}
        rowsPerPage={pagerInfo ? pagerInfo.limit : 0}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
      {assignTarget && (
        <AssignWasherDialog
          orderId={assignTarget.orderId}
          lat={assignTarget.lat}
          long={assignTarget.long}
          serviceId={assignTarget.service_id}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => {
            setAssignTarget(null);
            if (typeof onAssigned === 'function') onAssigned();
          }}
        />
      )}
    </Card>
  );
};

JobsListResults.propTypes = {
  jobs: PropTypes.array,
  handleRow: PropTypes.func,
  loading: PropTypes.bool,
  pagerInfo: PropTypes.object,
  handlePagerInfo: PropTypes.func,
};

export default JobsListResults;
